import type { Ref } from 'react';
import { useI18n } from '@/lib/i18n';
import { formatExpiryNotice } from '../wallet-format';
import type { WalletStatus } from './BalancePanel';
import { CreditLotsCard } from './CreditLotsCard';
import { LedgerCard } from './LedgerCard';

export type BillingRecordsTab = 'records' | 'credits';

/**
 * Payment records and credits, one card with two tabs.
 *
 * They were two full cards that listed every top-up twice (once as a payment
 * with its receipt, once as a credit with what is left of it), and the second
 * one pushed the records below the calculator and the rate card. One card
 * keeps both a click apart, right under the account card.
 *
 * Both bodies stay mounted and the hidden one is only hidden: switching tabs
 * must not drop the page of records the user turned to, nor refetch.
 */
export function BillingRecordsCard({ status, tab, onTab, cardRef }: {
  status: WalletStatus | null;
  tab: BillingRecordsTab;
  onTab: (tab: BillingRecordsTab) => void;
  cardRef: Ref<HTMLDivElement>;
}) {
  const { t, locale } = useI18n();
  // A LINE OF TEXT, NOT A NOTICE: an expiry date is a fact about money that
  // is sitting here, scheduled, with nothing wrong and nothing to do, so it is
  // not dressed as a warning. It sits in this header because the credits tab
  // is where those credits are listed.
  const expiryNotice = formatExpiryNotice(t, status?.expiringSoon, locale);
  const tabs: { id: BillingRecordsTab; label: string }[] = [
    { id: 'records', label: t('dashboard.wallet.ledger.billingTitle') },
    { id: 'credits', label: t('dashboard.wallet.lots.title') },
  ];

  return (
    <div className="wallet-card wallet-card--full wallet-records" ref={cardRef}>
      <div className="wallet-card__header wallet-records__header">
        <div className="wallet-records__tabs" role="tablist">
          {tabs.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              role="tab"
              id={`billing-tab-${id}`}
              aria-selected={tab === id}
              aria-controls={`billing-panel-${id}`}
              className="wallet-records__tab"
              onClick={() => onTab(id)}
            >
              {label}
            </button>
          ))}
        </div>
        {expiryNotice && <span className="wallet-card__header-stat">{expiryNotice}</span>}
      </div>
      <div role="tabpanel" id="billing-panel-records" aria-labelledby="billing-tab-records" hidden={tab !== 'records'}>
        <LedgerCard kind="billing" bare />
      </div>
      <div role="tabpanel" id="billing-panel-credits" aria-labelledby="billing-tab-credits" hidden={tab !== 'credits'}>
        <CreditLotsCard bare />
      </div>
    </div>
  );
}
